// Day.js
import dayjs from "dayjs";
// Fhir front library
import { SearchableTable } from "@fyrstain/hl7-front-library";
// Font awesome
import { faEye } from "@fortawesome/free-solid-svg-icons";
// Navigation
import { useNavigate } from "react-router-dom";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback } from "react";
// Fhir
import { EpisodeOfCare } from "fhir/r4";
// Component
import AcesoPage from "src/components/AcesoPage/AcesoPage";

const StaysPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const onDetails = useCallback(
    (id: string) => {
      navigate(`/Pathway/Stays/${id}`);
    },
    [navigate],
  );

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    console.error(
      "EpisodeOfCare error :",
      error ?? "Network or FHIR error",
    );
  }, []);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  function getPatient(episode: EpisodeOfCare): string {
    return (
      episode.patient?.display ??
      episode.patient?.identifier?.value ??
      episode.patient?.reference ??
      i18n.t("general.notavailable")
    );
  }

  function formatDate(value?: string): string {
    if (!value) {
      return i18n.t("general.notavailable");
    }

    return dayjs(value).format("DD/MM/YYYY HH:mm");
  }

  function getStatus(episode: EpisodeOfCare): string {
    if (!episode.status) {
      return i18n.t("general.notavailable");
    }

    return i18n.t(`episodeofcare.status.${episode.status}`, {
      defaultValue: episode.status,
    });
  }

  function getType(episode: EpisodeOfCare): string {
    return (
      episode.type?.[0]?.text ??
      episode.type?.[0]?.coding?.[0]?.display ??
      episode.type?.[0]?.coding?.[0]?.code ??
      i18n.t("general.notavailable")
    );
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <AcesoPage
        loading={false}
        titleKey="pathway.tabs.stays"
        needsLogin={true}
      >
    <SearchableTable
      searchCriteriaProperties={{
        title: i18n.t("title.searchcriteria"),
        submitButtonLabel: i18n.t("button.search"),
        resetButtonLabel: i18n.t("button.reset"),
        language: i18n.t,
        fixedParameters: {
          _sort: "-date",
        },
        inputs: [
          {
            label: i18n.t("label.patient"),
            type: "text",
            searchParamsName: "patient.name",
            placeholder: i18n.t("placeholder.patientname"),
          },
          {
            label: i18n.t("label.date"),
            type: "date",
            searchParamsName: "date",
          },
          {
            label: i18n.t("label.status"),
            type: "select",
            searchParamsName: "status",
            options: [
              {
                label: i18n.t("episodeofcare.status.planned"),
                value: "planned",
              },
              {
                label: i18n.t("episodeofcare.status.waitlist"),
                value: "waitlist",
              },
              {
                label: i18n.t("episodeofcare.status.active"),
                value: "active",
              },
              {
                label: i18n.t("episodeofcare.status.onhold"),
                value: "onhold",
              },
              {
                label: i18n.t("episodeofcare.status.finished"),
                value: "finished",
              },
              {
                label: i18n.t("episodeofcare.status.cancelled"),
                value: "cancelled",
              },
              {
                label: i18n.t("episodeofcare.status.entered-in-error"),
                value: "entered-in-error",
              },
            ],
          },
        ],
      }}
      paginatedTableProperties={{
        columns: [
          {
            header: i18n.t("label.patient"),
            dataField: "patient",
            width: "20%",
          },
          {
            header: i18n.t("label.startdate"),
            dataField: "startDate",
            width: "18%",
          },
          {
            header: i18n.t("label.enddate"),
            dataField: "endDate",
            width: "18%",
          },
          {
            header: i18n.t("label.status"),
            dataField: "status",
            width: "15%",
          },
          {
            header: i18n.t("label.type"),
            dataField: "type",
            width: "18%",
          },
        ],
        action: [
          {
            icon: faEye,
            onClick: onDetails,
          },
        ],
        mapResourceToData: (resource: EpisodeOfCare) => ({
          id: resource.id,
          patient: getPatient(resource),
          startDate: formatDate(resource.period?.start),
          endDate: formatDate(resource.period?.end),
          status: getStatus(resource),
          type: getType(resource),
        }),
        searchProperties: {
          serverUrl: process.env.REACT_APP_FHIR_URL as string,
          resourceType: "EpisodeOfCare",
        },

        onError,
      }}
    />
    </AcesoPage>
  );
};

export default StaysPage;